import { useCallback, useEffect, useState } from 'react';
import { DeliveryOption, ShippingType } from '../../../clients/quipli-service/delivery/delivery';
import { useDeliveryApi } from '../../../clients/quipli-service/delivery/delivery-api';
import usePrevious from '../../../hooks/use-previous';
import { debounce } from 'lodash';
import deepEqual from 'deep-equal';
import { SelectForm } from '../../../components/select-form/select-form';
import { Typography } from '../../../components/typography/typography';
import { components } from 'react-select';
import Icon from '../../../components/icons/icon';
import { toast } from '../../../util/toasts';
import { OrderType } from '../../../clients/quipli-service/order/order';

export const OrderDeliverySelection = ({
  productTypes,
  locationId,
  selected,
  setSelected,
  setDeliveryDescriptions,
  placeholder = 'Select...',
}: {
  productTypes: { type: OrderType; id: number }[];
  locationId?: number;
  selected: ShippingType | null;
  setSelected: (s: ShippingType | null) => void;
  setDeliveryDescriptions: (ds: { productId: number; text: string }[]) => void;
  placeholder?: string;
}) => {
  const { getDeliveryOptions } = useDeliveryApi();

  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[]>([]);
  const value = deliveryOptions.find((_) => _.type === selected) || null;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const prevProductIds = usePrevious(productTypes);

  const doSearch = useCallback(
    debounce(async (productTypes: { type: OrderType; id: number }[], locationId: number) => {
      setIsLoading(true);
      try {
        const res = await getDeliveryOptions(productTypes, locationId, selected || ShippingType.SelfPickUp);

        setDeliveryOptions(res);

        const firstOcurrence = res.find((_) => _.isSelected) || res[0] || null;

        if (!selected && !!firstOcurrence) {
          setSelected(firstOcurrence.type);
          setIsLoading(false);
          return;
        }

        const existsPrev = res.find((_) => _.type === selected);

        if (!existsPrev) {
          setSelected(firstOcurrence.type);
        }
      } catch (e) {
        toast('Failed to fetch delivery options', 'error');
      } finally {
        setIsLoading(false);
      }
    }, 300),
    [selected, isLoading],
  );

  useEffect(() => {
    if (deepEqual(prevProductIds, productTypes)) return;

    if (!locationId || !productTypes.length) {
      setDeliveryOptions([]);
      setSelected(null);
      return;
    }

    doSearch(productTypes, locationId);
  }, [productTypes, locationId]);

  useEffect(() => {
    setDeliveryDescriptions(
      value && value.descriptions
        ? value.descriptions?.map((d) => ({
            productId: d.productId,
            text: d.label,
          }))
        : [],
    );
  }, [value]);

  return (
    <>
      <SelectForm
        menuShouldBlockScroll
        menuPosition="fixed"
        menuPortalTarget={document.body}
        placeholder={placeholder}
        isSearchable={false}
        isLoading={isLoading}
        isDisabled={productTypes.length === 0}
        options={deliveryOptions}
        value={value}
        getOptionValue={(o) => `${o.type}`}
        onChange={(o) => setSelected(o?.type || null)}
        components={{
          Option: (props) => (
            <components.Option {...props}>
              <div className="flex items-center gap-[6px] justify-between">
                <div className="flex flex-col">
                  <Typography type="text-sm" className="text-quipli-black-700">
                    {props.data.label}
                  </Typography>
                  <Typography type="text-sm" className="text-quipli-black-500">
                    {props.data.additionalText}
                  </Typography>
                </div>

                {props.isSelected && <Icon icon="Check" stroke="var(--quipli-black-800)" />}
              </div>
            </components.Option>
          ),
        }}
        dataCy="order-delivery-selection"
      />

      {value?.additionalText && (
        <Typography type="text-sm" className="text-grey-500">
          {value.additionalText}
        </Typography>
      )}
    </>
  );
};
